import React, { useEffect, useState } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Gift, Coins, Trophy, ExternalLink, Check, Clock, Zap, ShoppingBag, ArrowDownToLine, Sparkles } from 'lucide-react';

const TASK_ICONS = {
  daily_checkin: Zap, youtube_subscribe: ExternalLink, youtube_watch: ExternalLink,
  tiktok_follow: ExternalLink, facebook_follow: ExternalLink, instagram_follow: ExternalLink,
  telegram_join: ExternalLink, discord_join: ExternalLink, twitter_follow: ExternalLink,
  visit_website: ExternalLink, external_link: ExternalLink, watch_video: ExternalLink,
  survey: Gift, quiz: Gift, referral: Trophy,
};

export const TaskingPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [packs, setPacks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [wdCoins, setWdCoins] = useState('');
  const [wdCurrency, setWdCurrency] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, p, t, h, c] = await Promise.all([
        userAPI.getTaskingDashboard(),
        userAPI.listTaskingPacks(),
        userAPI.listTaskingTasks().catch(() => ({ data: [] })),
        userAPI.taskingHistory().catch(() => ({ data: [] })),
        publicAPI.getCurrencies(true),
      ]);
      setDashboard(d.data);
      setPacks(p.data);
      setTasks(t.data);
      setHistory(h.data);
      setCurrencies(c.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const buyPack = async (pack) => {
    if (!window.confirm(`Buy ${pack.name} for ${pack.price} ${pack.currency}?`)) return;
    try {
      await userAPI.buyTaskingPack(pack.id);
      toast.success('Pack activated!');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const doTask = async (task) => {
    if (task.url) {
      window.open(task.url, '_blank', 'noopener,noreferrer');
    }
    try {
      const res = await userAPI.completeTask(task.id, {});
      if (res.data.status === 'approved') {
        toast.success(`+${res.data.reward} ${dashboard?.config?.coin_symbol || 'coins'} earned!`);
      } else {
        toast.info('Submitted for admin review.');
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const submitWithdraw = async () => {
    const c = parseFloat(wdCoins);
    if (!c || !wdCurrency) return toast.error('Enter amount and currency');
    setBusy(true);
    try {
      const res = await userAPI.taskingWithdraw(c, wdCurrency);
      toast.success(`Received ${res.data.amount.toFixed(2)} ${res.data.currency}`);
      setShowWithdraw(false); setWdCoins('');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
    finally { setBusy(false); }
  };

  if (loading || !dashboard) {
    return <FocusedLayout title="Task Rewards"><p className="text-center py-12 text-muted-foreground">Loading rewards...</p></FocusedLayout>;
  }

  const cfg = dashboard.config || {};
  const w = dashboard.wallet || { balance: 0, lifetime_earned: 0, total_withdrawn: 0 };
  const activePack = dashboard.active_packs?.[0];
  const dailyEarnedPct = activePack?.max_daily_earnings > 0
    ? Math.min(100, (activePack.coins_earned_today / activePack.max_daily_earnings) * 100)
    : 0;
  const availableRates = Object.entries(cfg.exchange_rates || {});

  return (
    <FocusedLayout title="Task Rewards">
      <div className="space-y-6" data-testid="tasking-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sparkles className="h-8 w-8 text-amber-500" /> Rewards & Tasks
            </h1>
            <p className="mt-2 text-muted-foreground">Complete tasks, earn <strong>{cfg.coin_name}</strong>, cash out to your main wallet.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowWithdraw(true)} disabled={w.balance <= 0} data-testid="withdraw-coins-btn">
              <ArrowDownToLine className="h-4 w-4 mr-2" /> Withdraw
            </Button>
          </div>
        </div>

        {/* Wallet strip */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent" data-testid="rewards-balance-card">
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Coins className="h-3 w-3" /> {cfg.coin_symbol || 'TC'} Balance
              </p>
              <p className="text-3xl font-semibold mt-1 text-amber-700 dark:text-amber-400" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="rewards-balance">
                {w.balance.toFixed(0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{cfg.coin_name}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Lifetime earned</p>
              <p className="text-3xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{w.lifetime_earned.toFixed(0)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total withdrawn</p>
              <p className="text-3xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{w.total_withdrawn.toFixed(0)}</p>
            </CardContent>
          </Card>
          <Card data-testid="active-pack-card">
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Active pack</p>
              {activePack ? (
                <>
                  <p className="text-xl font-semibold mt-1 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{activePack.pack_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">Expires in {activePack.days_remaining} day{activePack.days_remaining !== 1 ? 's' : ''}</p>
                </>
              ) : (
                <p className="text-lg font-semibold text-muted-foreground mt-1">No active pack</p>
              )}
            </CardContent>
          </Card>
        </div>

        {activePack && activePack.max_daily_earnings > 0 && (
          <Card>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between mb-2 text-sm">
                <span>Today&apos;s earnings</span>
                <span className="font-medium">{activePack.coins_earned_today.toFixed(0)} / {activePack.max_daily_earnings} {cfg.coin_symbol}</span>
              </div>
              <Progress value={dailyEarnedPct} data-testid="daily-progress" />
              <p className="text-xs text-muted-foreground mt-2">
                {activePack.tasks_completed_today} task{activePack.tasks_completed_today !== 1 ? 's' : ''} completed today
                {activePack.max_tasks_per_day ? ` (max ${activePack.max_tasks_per_day}/day)` : ''}
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue={activePack ? 'tasks' : 'packs'}>
          <TabsList>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
            <TabsTrigger value="packs" data-testid="tab-packs">Pack Store</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks">
            <Card>
              <CardContent className="pt-6">
                {!activePack ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground opacity-40" />
                    <p className="mt-3 text-muted-foreground">Buy a pack to unlock tasks.</p>
                  </div>
                ) : tasks.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No tasks available right now.</p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {tasks.map(t => {
                      const Icon = TASK_ICONS[t.task_type] || Sparkles;
                      const done = t.completed_today >= (t.daily_limit || 1);
                      return (
                        <div key={t.id} className="p-4 rounded-lg border border-border flex flex-col" data-testid={`task-${t.id}`}>
                          <div className="flex items-start gap-3 mb-2">
                            <div className="h-10 w-10 rounded-lg bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <p className="font-medium">{t.title}</p>
                                {t.featured && <Badge variant="outline" className="bg-amber-500/15 text-amber-600 border-amber-500/30">Featured</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">{t.description || t.instructions}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-auto pt-2 gap-2">
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                              +{t.reward_coins} {cfg.coin_symbol}
                            </Badge>
                            {done ? (
                              <Button size="sm" variant="outline" disabled><Check className="h-3 w-3 mr-1" /> Done</Button>
                            ) : (
                              <Button size="sm" onClick={() => doTask(t)} data-testid={`complete-task-${t.id}`}>
                                {t.url ? <><ExternalLink className="h-3 w-3 mr-1" /> Open &amp; earn</> : <>Complete</>}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="packs">
            <div className="grid gap-4 md:grid-cols-3">
              {packs.map(p => (
                <Card key={p.id} className={p.featured ? 'border-amber-500/40' : ''} data-testid={`pack-${p.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{p.name}</CardTitle>
                      {p.badge && <Badge variant="outline" className="capitalize">{p.badge}</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-3 min-h-[2rem]">{p.description}</p>
                    <p className="text-3xl font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {p.price} <span className="text-sm text-muted-foreground">{p.currency}</span>
                    </p>
                    <div className="text-xs text-muted-foreground space-y-1 my-3">
                      <p>+{p.coins_issued} {cfg.coin_symbol} on activation</p>
                      <p>Duration: {p.duration_days} days</p>
                      {p.max_daily_earnings > 0 && <p>Max {p.max_daily_earnings} {cfg.coin_symbol}/day</p>}
                      {p.max_tasks_per_day > 0 && <p>Max {p.max_tasks_per_day} tasks/day</p>}
                      {p.min_withdrawal > 0 && <p>Min withdrawal: {p.min_withdrawal} {cfg.coin_symbol}</p>}
                    </div>
                    <Button className="w-full" onClick={() => buyPack(p)} data-testid={`buy-pack-${p.id}`}>
                      <ShoppingBag className="h-4 w-4 mr-2" /> Buy Pack
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {packs.length === 0 && <p className="text-center py-8 text-muted-foreground col-span-3">No packs available.</p>}
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardContent className="pt-6">
                {history.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No activity yet.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {history.map(h => (
                      <div key={h.id} className="py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium capitalize">{h.type.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">{h.description} · {formatDate(h.created_at)}</p>
                        </div>
                        <p className={`font-semibold ${h.coins > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {h.coins > 0 ? '+' : ''}{h.coins.toFixed(0)} {cfg.coin_symbol}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw {cfg.coin_name}</DialogTitle>
            <DialogDescription>Convert your {cfg.coin_symbol} balance into a wallet currency using the admin exchange rate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount ({cfg.coin_symbol})</Label>
              <Input type="number" step="1" value={wdCoins} onChange={(e) => setWdCoins(e.target.value)} data-testid="wd-coins-input" />
              <p className="text-xs text-muted-foreground mt-1">Available: {w.balance.toFixed(0)} {cfg.coin_symbol}</p>
            </div>
            <div>
              <Label>Convert to</Label>
              <Select value={wdCurrency} onValueChange={setWdCurrency}>
                <SelectTrigger data-testid="wd-currency-select"><SelectValue placeholder="Pick currency" /></SelectTrigger>
                <SelectContent>
                  {availableRates.map(([cur, rate]) => (
                    <SelectItem key={cur} value={cur}>{cur} · 1 {cfg.coin_symbol} = {rate} {cur}</SelectItem>
                  ))}
                  {availableRates.length === 0 && <SelectItem value="none" disabled>No rates configured</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {wdCoins && wdCurrency && cfg.exchange_rates?.[wdCurrency] && (
              <p className="text-sm p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
                You will receive <strong>{(parseFloat(wdCoins) * cfg.exchange_rates[wdCurrency]).toFixed(2)} {wdCurrency}</strong> in your main wallet.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdraw(false)}>Cancel</Button>
            <Button onClick={submitWithdraw} disabled={busy} data-testid="confirm-withdraw-btn">
              {busy ? 'Processing...' : 'Withdraw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FocusedLayout>
  );
};
